import { ErrorLevel } from '@griply/utils';
import { signInWithCustomToken } from 'firebase/auth';
import { QueryParamOptions } from 'shared/lib/react-router-dom';
import { getErrorReporter } from 'shared/services/error-tracking';
import {
  clearDesktopSignIn,
  getDesktopSignIn,
} from 'shared/services/local-storage';
import { DESKTOP_CODE_PARAM } from 'shared/utils/get-desktop-sign-in-urls';
import { removeSearchParamFromUrl } from 'shared/utils/remove-search-param-from-url';

import { getAuthentication } from './helpers';
import { channelLogin } from './helpers/auth';
import { redeemDesktopSignIn } from './redeem-desktop-sign-in';

export enum DesktopSignInResult {
  NotAttempted = 'not-attempted',
  SignedIn = 'signed-in',
  Failed = 'failed',
}

export const signInWithDesktopCode = async (): Promise<DesktopSignInResult> => {
  const searchParams = new URLSearchParams(window.location.search);
  const code = searchParams.get(DESKTOP_CODE_PARAM);
  const challenge = searchParams.get(QueryParamOptions.DesktopChallenge);

  if (!code) {
    return DesktopSignInResult.NotAttempted;
  }

  removeSearchParamFromUrl(DESKTOP_CODE_PARAM);
  removeSearchParamFromUrl(QueryParamOptions.DesktopChallenge);

  const desktopSignIn = getDesktopSignIn();

  // Redeeming spends the code whatever the outcome, so a hand-off we cannot match is refused before the call.
  if (!desktopSignIn || desktopSignIn.challenge !== challenge) {
    getErrorReporter().captureMessage(
      'desktop sign-in rejected: no matching hand-off stored for the incoming code',
      ErrorLevel.Warning,
      {
        tags: { source: 'desktop-sign-in-verifier' },
        fingerprint: ['desktop-sign-in-verifier'],
      },
    );
    return DesktopSignInResult.Failed;
  }

  clearDesktopSignIn();

  try {
    const token = await redeemDesktopSignIn(code, desktopSignIn.verifier);

    const auth = await getAuthentication();
    await signInWithCustomToken(auth, token);
  } catch (error) {
    getErrorReporter().captureException(error, {
      tags: { source: 'desktop-sign-in-redeem' },
    });
    return DesktopSignInResult.Failed;
  }

  channelLogin();

  return DesktopSignInResult.SignedIn;
};
